import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setAuthToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Hard ceiling on how long we'll wait for /api/auth/google/session.
// 15s timeout: matches the backend httpx timeout to Emergent Auth.
const AUTH_CALLBACK_TIMEOUT_MS = 15000;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = match[1];

    // Strip the session_id from the visible URL IMMEDIATELY so a
    // refresh during the call doesn't re-attempt the same dead
    // session_id (and so the URL bar never leaks a credential
    // fragment to bookmarks / share sheets / browser history).
    // We use replaceState here because we don't yet want
    // react-router to remount — App.js will keep the AuthCallback
    // mounted via the `hasSessionInHash` defense-in-depth check
    // (which now reads `liveHash`, so this strip immediately
    // satisfies the OR check too). The success branch still calls
    // navigate({...}) to sync react-router's state.
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (_) {}

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AUTH_CALLBACK_TIMEOUT_MS);

    api.post("/auth/google/session", { session_id }, { signal: controller.signal })
      .then(({ data }) => {
        clearTimeout(timer);
        if (data?.token) setAuthToken(data.token);
        if (setUser) setUser(data);
        navigate({ pathname: "/", hash: "", search: "" }, {
          replace: true,
          state: { user: data },
        });
      })
      .catch((e) => {
        clearTimeout(timer);
        // Two distinct failure modes — show the user which:
        //   • timeout / network → "Sign-in is taking too long.
        //                          Tap below to try again."
        //   • 401 from backend  → "Sign-in expired. Try again."
        // In both cases we surface a single "Try again" button that
        // re-enters the Google flow instead of dropping the user on
        // a bare login form with no explanation.
        const isAbort = e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || /abort|timeout/i.test(e?.message || "");
        setError(isAbort ? "timeout" : "rejected");
      });

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [navigate, setUser]);

  // Fast-fail UI — replaces the indefinite spinner with a clear
  // explanation and a one-tap recovery.
  if (error) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6">
        <div className="text-center max-w-sm" data-testid="auth-callback-error">
          <p className="font-heading text-2xl text-forest">
            {error === "timeout" ? "Sign-in is taking too long." : "Sign-in expired."}
          </p>
          <p className="text-muted_ink text-sm mt-2">
            {error === "timeout"
              ? "The Google sign-in step timed out. Tap below to try again."
              : "Your Google session expired. Tap below to start a fresh sign-in."}
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <button
              data-testid="auth-callback-retry"
              onClick={() => {
                const redirectUrl = window.location.origin + "/";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="h-11 px-5 text-forest hover:text-terracotta font-medium text-sm"
            >
              Try sign-in again
            </button>
            <button
              data-testid="auth-callback-back-to-login"
              onClick={() => navigate("/login", { replace: true })}
              className="h-11 px-5 rounded-full text-muted_ink text-sm hover:bg-cream-200"
            >
              Use email instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] grid place-items-center px-6">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto rounded-full border-4 border-forest/20 border-t-forest animate-spin" />
        <p className="font-heading text-2xl text-forest mt-6">Signing you in…</p>
        <p className="text-muted_ink text-sm mt-1">One moment.</p>
      </div>
    </div>
  );
}
